import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AccountService, AuthService } from '@okcargo-apps/shared-application-ionic';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';

export const authGuard: CanActivateFn = async (route): Promise<boolean | UrlTree> => {
  const accountService = inject(AccountService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredCapability = route.data['requiredCapability'] as ECapabilities | undefined;
  const requiredCapabilities = route.data['requiredCapabilities'] as ECapabilities[] | undefined;
  const fallbackUrlTree = router.createUrlTree(['/']);
  const loginUrlTree = router.createUrlTree(['/login']);

  const hasAccess = (): boolean => {
    if (requiredCapabilities?.length) {
      return requiredCapabilities.some((capability) => accountService.hasCapability(capability));
    }
    if (requiredCapability) {
      return accountService.hasCapability(requiredCapability);
    }
    return true;
  };

  try {
    // The session is resolved in one place (AuthService), so this guard only decides where the user
    // goes with the result. The token of the URL (a session received by link) is part of the
    // resolution, as it was here.
    const resolution = await authService.resolveSession(route.queryParamMap.get('token'));

    if (resolution === 'no-credentials' || resolution === 'rejected') {
      // The snapshot must not let the user in without a verified session, or the app would bounce
      // between the dashboard and the login.
      console.warn(`Unauthorized: the session could not be resolved (${resolution})`);

      return loginUrlTree;
    }

    return hasAccess() ? true : fallbackUrlTree;
  } catch (error) {
    console.warn('Unauthorized: Error during authentication flow', error);
    return loginUrlTree;
  }
};
